import React from 'react'
import { formatDate } from '../utils/dateUtils';
import { formatAmount } from '../utils/formatAmount';
import { mapShipStatus } from '../utils/shipStatusDisplay';

const style = 'px-1 py-2 md:px-3 md:py-4 text-xxs text-gray-800 border-r border-gray-300 text-left align-top font-medium md:text-sm 3xl:text-xl';

const OrderSummaryRow = ({order, shop, isAdmin = false, onOpenOrderDetail}) => {

  const deliveryRate = order.isCod ? order.codRate : order.productRate;
  const isSendOnly = order.isSendOnly;
  const productOnly = order.showOnlyProductAmount === true;
  // Oversize item: weight price comes from dimension measurement in Thailand
  // (admin sets weightPrice manually) — never show a weight * weightRate estimate.
  const awaitingDimension = !!order.isOversize && !order.weightPrice && !order.weightPaid;
  // Shipping mode for the icon. When a ShipDate round is assigned, trust it as the
  // source of truth (isAir); otherwise fall back to hand-carry (which always ships by
  // air) so orders without an assigned round still show a mode.
  const isAir = order.ShipDate ? !!order.ShipDate.isAir : !!order.handCarry;

  // ส่งตรวจ fee (extra JP fees) — flat THB, collected together with the product payment,
  // so it folds into the same block with ONE payment state instead of its own รอชำระ.
  const extraJpFees = Array.isArray(order.extraJpFees) ? order.extraJpFees : [];
  const feeTotalThb = extraJpFees.reduce((s, f) => s + (Number(f.amountThb) || 0), 0);
  const hasFreeInspection = extraJpFees.length > 0 && feeTotalThb === 0;

  return (
    <>
      <tr className={`${order.shipStatus === 'Cancel by store' ? 'bg-red-100' : isAdmin ? 'bg-blue-100' : ''}`}>
        <td className={`${style}`}>
          {formatDate(order.orderDate)}
        </td>
        {isSendOnly ?
          <>
            <td className={`${style}`}>สินค้าฝากส่ง</td>
            <td className={`${style}`}></td>
            <td className={`${style}`}>เลขพัสดุญี่ปุ่น</td>
            <td className={`${style}`}>
              {order.deliveryFeeJp ? (
                <span>{`${order.deliveryFeeJp} เยน (${order.isCod ? order.codRate : order.productRate})`}</span>
              ) : ''}
            </td>
          </> : <>
            <td className={`${style}`}>
              <a href={order.url} target="_blank" className="text-indigo-600 hover:text-indigo-900">
                สิ้งค์สั่งซื้อ
              </a>
            </td>
            <td className={`${style}`}>{order.qty}</td>
            <td className={`${style}`}>{formatAmount(order.amount * order.qty) + ' เยน (' + order.productRate + ')'} </td>
            <td className={`${style}`}>{order.deliveryFeeJp === 0 ? 'Free' : (order.deliveryFeeJp === null ? 'รอค่าส่ง' : formatAmount(order.deliveryFeeJp) + ' เยน') + ' (' + deliveryRate + ')'}</td>
          </>
        }
        
        <td className={`${style} ${!order.weightPaid && !order.weightPrice && order.weight !== 0 && !order.weight ? 'bg-red-100' : ''}`}>
          {order.weightPaid ? (
            `${order.weight ? `${order.weight} kg ` : '' }(${order.weightPrice ? 'ราคาเหมา' : order.weightRate})`
          ) : order.weightPrice ? (
            `${order.weight ? `${order.weight} kg ` : '' }(ราคาเหมา)`
          ) : awaitingDimension ? (
            `${order.weight ? `${order.weight} kg ` : '' }(รอวัดขนาด)`
          ) : order.weight === 0 ?(
            ''
          ) : order.weight === null ? 'รอน้ำหนัก' : `${order.weight} kg (${order.weightRate})`}
        </td>
        <td className={`${style} `}>{order.trackingTh}</td>
        <td className={`${style}`}>
          {order.ShipDate && formatDate(order.ShipDate.shipDate)}
          {isAdmin && (
            <span className="ml-1" title={isAir ? 'เครื่องบิน (Air)' : 'เรือ (Ship)'}>
              {isAir ? '✈️' : '🚢'}
            </span>
          )}
        </td>
        <td className={`${style}`}>{order.ShipDate && formatDate((order.ShipDate || {}).thaiDate || order.ShipDate.expectThaiDate)}</td>
      </tr>
      
      <tr className={`${order.shipStatus === 'Cancel by store' ? 'bg-red-100' : isAdmin ? 'bg-blue-100' : ''}`}>
        
        <td className={`${style}`}>
          <div className="whitespace-pre-line">{mapShipStatus(order.shipStatus, isAir, order)}</div>
          {isAdmin && (
            <>
              <div>{order.smileNameInput}</div>
              <div>{order.trackingJp}</div>
              {onOpenOrderDetail && (
                <button
                  onClick={() => onOpenOrderDetail(order)}
                  className="mt-2 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  ดูรายละเอียด
                </button>
              )}
            </>
          )}
        </td>
        {isSendOnly ?
        <>
          <td className={`${style}`}></td>
          <td className={`${style}`}></td>
          <td className={`${style}`}>{order.trackingJp}</td>
          <td className={`${style}`}>
            <div className={`${order.deliveryFeeJp && order.shipJpPaid === 'รอชำระ' ? 'text-red-600' : ''}`}>
              <span>{`${order.deliveryFeeJp ? formatAmount(order.deliveryFeeJp * (order.isCod ? order.codRate : order.productRate)) +' บาท' : ''}`}</span>
              <div>{order.deliveryFeeJp && order.shipJpPaid === 'รอชำระ' ? '(รอชำระ)' : ''}</div>
            </div>
          </td>
        </> : <>
          <td className='py-1 md:py-1 border-r border-gray-300'>
            <img className='max-w-12 max-h-12 md:max-h-20 md:max-w-20 object-contain' src={order.imageUrl} />
          </td>
          <td className={`${style}`}></td>
          <td className={`${style}`}> {/* ค่าสินค้าไทย */}
            {order.productPayment === 'รอชำระ' ? (
              <div className='text-red-600'>
                <span>{formatAmount(order.amount * order.productRate * order.qty)} {shop === 'tama' ? "" : 'บาท'}</span>
                { shop === 'tama' &&
                    <span>+ {formatAmount(order.transactionFee)} บาท</span>
                }
                {feeTotalThb > 0 && (
                  <div>+ ส่งตรวจ {formatAmount(feeTotalThb)} บาท</div>
                )}
                {(shop === 'tama' || feeTotalThb > 0) && (
                  <div>{formatAmount(order.amount * order.productRate * order.qty + (shop === 'tama' ? order.transactionFee : 0) + feeTotalThb)} บาท</div>
                )}
                {hasFreeInspection && <div className='text-green-700'>+ ส่งตรวจ (ฟรี)</div>}
                <div>(รอชำระ)</div>
              </div>
            ) : order.productPayment === 'มัดจำ 50%' ? (
              <div>
                <span>{formatAmount(order.amount * order.productRate * order.qty)} {shop === 'tama' ? "" : 'บาท'}</span>
                { shop === 'tama' ?
                  <>
                    <span>+ {formatAmount(order.transactionFee)} บาท</span>
                    <div>{formatAmount(order.amount * order.productRate * order.qty + order.transactionFee)} บาท</div>
                  </> : <>

                  </>
                }
                <div className='text-red-600'>
                  {formatAmount((order.amount * order.productRate * order.qty) * 0.5)} บาท
                </div>
                {!order.extraJpFeesPaid && feeTotalThb > 0 && (
                  <>
                    <div className='text-red-600'>+ ส่งตรวจ {formatAmount(feeTotalThb)} บาท</div>
                    <div className='text-red-600'>{formatAmount((order.amount * order.productRate * order.qty) * 0.5 + feeTotalThb)} บาท</div>
                  </>
                )}
                {hasFreeInspection && <div className='text-green-700'>+ ส่งตรวจ (ฟรี)</div>}
                <div className='text-red-600'>(รอชำระ)</div>
              </div>
            ) : order.productPayment === 'มัดจำ 30%' ? (
              <div>
                { shop === 'tama' ?
                  <>
                    <span>{formatAmount(order.amount * order.productRate * order.qty)} + {formatAmount(order.transactionFee)} บาท</span>
                    <div>{formatAmount(order.amount * order.productRate * order.qty + order.transactionFee)} บาท</div>
                  </> : <>

                  </>
                }
                <div className='text-red-600'>
                  {formatAmount((order.amount * order.productRate * order.qty) * 0.7)} บาท
                </div>
                {!order.extraJpFeesPaid && feeTotalThb > 0 && (
                  <>
                    <div className='text-red-600'>+ ส่งตรวจ {formatAmount(feeTotalThb)} บาท</div>
                    <div className='text-red-600'>{formatAmount((order.amount * order.productRate * order.qty) * 0.7 + feeTotalThb)} บาท</div>
                  </>
                )}
                {hasFreeInspection && <div className='text-green-700'>+ ส่งตรวจ (ฟรี)</div>}
                <div className='text-red-600'>(รอชำระ)</div>
              </div>
            ) : (
              <>
                <span>{formatAmount(order.amount * order.productRate * order.qty)} + {formatAmount(order.transactionFee)} บาท</span>
                {feeTotalThb > 0 && order.extraJpFeesPaid && (
                  <div>+ ส่งตรวจ {formatAmount(feeTotalThb)} บาท</div>
                )}
                <div>{formatAmount(order.amount * order.productRate * order.qty + order.transactionFee + (order.extraJpFeesPaid ? feeTotalThb : 0))} บาท</div>
                {feeTotalThb > 0 && !order.extraJpFeesPaid && (
                  <div className='text-red-600'>+ ส่งตรวจ {formatAmount(feeTotalThb)} บาท (รอชำระ)</div>
                )}
                {hasFreeInspection && <div className='text-green-700'>+ ส่งตรวจ (ฟรี)</div>}
              </>
            )}
          </td>

          <td className={`${style}`}>{productOnly ? (
            <span className='text-gray-400'>—</span>
          ) : order.deliveryFeeJp === 0 ? 'Free' : 
          order.deliveryFeeJp === null ? 
          (<span className='text-red-600'>(รอชำระ)</span>) : (
            order.shipJpPaid === 'รอชำระ' ? (
              <div className='text-red-600'>
                <span>{formatAmount(order.deliveryFeeJp * (order.isCod ? order.codRate : order.productRate))} บาท</span>
                <div>(รอชำระ)</div>
              </div>
            ) : order.shipJpPaid === 'มัดจำ 50%' ? (
              <div>
                <div>{formatAmount(order.deliveryFeeJp * (order.isCod ? order.codRate : order.productRate))} บาท</div>
                <div className='text-red-600'>
                  {formatAmount((order.deliveryFeeJp * (order.isCod ? order.codRate : order.productRate)) * 0.5)} บาท
                </div>
                <div className='text-red-600'>(รอชำระ)</div>
              </div>
            ) : order.shipJpPaid === 'มัดจำ 30%' ? (
              <div>
                <div>{formatAmount(order.deliveryFeeJp * (order.isCod ? order.codRate : order.productRate))} บาท</div>
                <div className='text-red-600'>
                  {formatAmount((order.deliveryFeeJp * (order.isCod ? order.codRate : order.productRate)) * 0.7)} บาท
                </div>
                <div className='text-red-600'>(รอชำระ)</div>
              </div>
            ) : (
              <div>{formatAmount(order.deliveryFeeJp * (order.isCod ? order.codRate : order.productRate))} บาท</div>
            )
          )} </td>
        </>
        }
        <td className={`${style}`}>
          {productOnly ? (
            <span className='text-gray-400'>—</span>
          ) : order.weightPaid ? (
            <div>
              {formatAmount(
                order.weightPrice ? 
                order.weightPrice : 
                (isSendOnly && order.weight < 1 ? order.weightRate : order.weight * order.weightRate)
              ) + ' บาท'}
            </div>
          ) : order.weightPrice ? (
            <div className='text-red-600'>
              <div>{formatAmount(order.weightPrice) + ' บาท'}</div>
              <div>(รอชำระ)</div>
            </div>
          ) : awaitingDimension ? (
            <div className='text-amber-600'>รอวัดขนาด</div>
          ) : order.weight === 0 || order.weight === null? (
            ''
          ) : (
            <div className='text-red-600'>
              <div>{formatAmount(isSendOnly && order.weight < 1 ? order.weightRate : order.weight * order.weightRate) + ' บาท'}</div>
              <div>(รอชำระ)</div>
            </div>
          )}
        </td>
        <td className={`${style} ${productOnly || order.shipThPaid || order.deliveryFeeTh === 0 || order.deliveryFeeTh === null ? '' : 'text-red-600'}`}>
          {productOnly ? (
            <span className='text-gray-400'>—</span>
          ) : (
            <>
              {order.deliveryFeeTh ? `${order.deliveryFeeTh} บาท` : ""}
              {!order.shipThPaid && order.deliveryFeeTh !== 0 && order.deliveryFeeTh !== null && (
                <div>(รอชำระ)</div>
              )}
            </>
          )}
        </td>

        <td className={`${style}`}></td>
        <td className={`${style}`}>{order.ShipDate ? ( order.ShipDate.thaiDate ? "" : "ประมาณ") : ""}</td>
      </tr>
      <tr className='bg-purple-400'>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
      </tr>
    </>
  )
}

export default OrderSummaryRow